import { ROLES } from 'app/auth/authRoles';
import { checkIsGlobalRole, checkIsInternalCompany } from 'app/auth/authUtils';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { LoanContactTypeEnum } from 'constants/loanContactTypes';
import { INTERNAL_ROLE_NAME_PREFIX } from 'constants/roleNames';
import searchItems from 'utils/searchItems';

import { getIsFromInternalCompany } from 'store/auth/selectors/master.selectors';
import { getDebouncedSearchValue } from 'store/search/selectors';

export const selectRoles = ({ roles }) => roles;

export const selectRolesList = createSelector(selectRoles, Object.values);

export const selectRolesById = createSelector(selectRolesList, rolesList =>
  _.keyBy(rolesList, 'id'),
);

export const selectRolesListExcludeInternal = createSelector(selectRolesList, roles =>
  roles.filter(role => !_.toLower(role.name).startsWith(INTERNAL_ROLE_NAME_PREFIX)),
);

export const selectGlobalRoles = createSelector(selectRolesListExcludeInternal, roles =>
  roles.filter(checkIsGlobalRole),
);

export const selectSecurityGroupOptions = createSelector(selectRolesListExcludeInternal, roles =>
  roles.map(role => ({
    name: `Security Group: ${role.name}`,
    value: role.id,
    // The new roles API returns companyId: null + a top-level isGlobal flag, so
    // checkIsInternalCompany (companyId-based) never detects a global role. Prefer
    // the API's own isGlobal, falling back to the legacy company check for old-shape data.
    isGlobal: role.isGlobal ?? checkIsInternalCompany(role),
  })),
);

const selectSortedSecurityGroupOptions = createSelector(
  selectSecurityGroupOptions,
  securityGroups => securityGroups.sort((a, b) => (a.name > b.name ? 1 : -1)),
);

export const selectFormattedSecurityGroupOptions = createSelector(
  selectSortedSecurityGroupOptions,
  securityGroups =>
    securityGroups.map(option => ({
      ...option,
      value: LoanContactTypeEnum.SecurityGroup,
      roleId: option.value,
    })),
);

export const selectExtendedRolesWithReadOnlyStatus = createSelector(
  [selectRolesList, getIsFromInternalCompany],
  (rolesList, isMasterFromInternalCompany) =>
    rolesList.reduce((acc, role) => {
      const isInternalRole = !isMasterFromInternalCompany && role.isGlobal;
      const isAdminRole = _.toLower(role.name) === ROLES.ADMIN && isMasterFromInternalCompany;
      const readOnly = isInternalRole || isAdminRole;

      acc[role.id] = { ...role, readOnly };

      return acc;
    }, {}),
);

const selectExtendedRolesList = createSelector(
  selectExtendedRolesWithReadOnlyStatus,
  Object.values,
);

const selectExtendedRolesListWithAdminEscape = createSelector(
  [selectExtendedRolesList, getIsFromInternalCompany],
  (rolesList, isMasterFromInternalCompany) =>
    rolesList.filter(
      role => !(role?.name?.toLowerCase() === ROLES.ADMIN && !isMasterFromInternalCompany),
    ),
);

const selectSortedRolesValues = createSelector(
  selectExtendedRolesListWithAdminEscape,
  rolesValues => _.sortBy(rolesValues, [role => _.toLower(role.name)]),
);

export const selectFilteredRolesValues = createSelector(
  [getDebouncedSearchValue, selectSortedRolesValues],
  searchItems(({ name, id }) => [name, id]),
);

export const selectRoleByNormalizedName = memoizeOne(normalizedRoleName =>
  createSelector(selectRolesList, rolesList =>
    rolesList.find(({ name }) => _.toUpper(name) === normalizedRoleName),
  ),
);

const selectMasterRole = ({ auth }) => auth?.master?.role ?? null;

/**
 * Returns the security group ids (roleIds) that correspond to the current user's
 * assigned role names. First-match precedence: the order of the user's roles
 * determines which group override applies at runtime.
 *
 * @type {import('reselect').OutputSelector}
 */
export const selectCurrentUserSecurityGroupIds = createSelector(
  selectRolesListExcludeInternal,
  selectMasterRole,
  (companyRoles, userRoles) => {
    const names = Array.isArray(userRoles) ? userRoles : [].concat(userRoles ?? []);
    const idByRoleName = new Map(companyRoles.map(role => [role.name, role.id]));

    // Resolve in the USER's role order so first-match precedence follows the user's roles
    // (matches the JSDoc above), not the company role-list order.
    return names.map(name => idByRoleName.get(name)).filter(Boolean);
  },
);
